import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Drawer } from 'antd';

import { convertISOToDateTimeWithoutSecondsWithTZ } from '../../../util/timezone';
import {
  HistoryTableStyled,
  PipelinesHistoryContentContainer,
  PipelinesHistoryHeaderContainer
} from './styled';
import { HISTORY_PAGINATION_DEFAULT, en } from './constants';

const PipelineHistoryDrawer = ({
  open,
  onClose,
  selectedPipeline,
  apiActions,
  pipelinesHistory,
  pipelinesHistoryIsLoading
}) => {
  const [expandedView, setExpandedView] = useState('');

  const [pagination, setPagination] = useState(HISTORY_PAGINATION_DEFAULT);

  const getAMLSCommonPipelinesHistoryPayload = useMemo(
    () => ({
      id: selectedPipeline?.id,
      limit: pagination.pageSize,
      offset: pagination.pageSize * (pagination.current - 1)
    }),
    [selectedPipeline?.id, pagination]
  );

  const getAMLSCommonPipelinesHistory = () => {
    apiActions.getAMLSCommonPipelinesHistory(
      getAMLSCommonPipelinesHistoryPayload
    );
  };

  useEffect(() => {
    if (open && selectedPipeline?.id) {
      getAMLSCommonPipelinesHistory();
    }
  }, [open, getAMLSCommonPipelinesHistoryPayload]);

  const handleViewClick = (id) => {
    setExpandedView(id);
  };

  const handleHideClick = () => {
    setExpandedView('');
  };

  const onPaginationChange = (current, pageSize) => {
    setPagination({ current, pageSize });
  };

  return (
    <Drawer
      title={
        <PipelinesHistoryHeaderContainer>
          <span className="title">{en.actions.history.modalTitle}</span>
          <Button
            type="primary"
            className="refresh-button"
            onClick={getAMLSCommonPipelinesHistory}>
            {en.actions.history.refreshList}
          </Button>
        </PipelinesHistoryHeaderContainer>
      }
      placement="right"
      width={472}
      closable={true}
      onClose={onClose}
      visible={open}
      destroyOnClose
      className="pipeline-history-drawer">
      <PipelinesHistoryContentContainer>
        <HistoryTableStyled
          id="pipeline-history-table"
          loading={pipelinesHistoryIsLoading}
          columns={[
            {
              title: '',
              dataIndex: 'instanceId',
              render: (value, history) => (
                <div key={value}>
                  <p>
                    Run at:{' '}
                    {convertISOToDateTimeWithoutSecondsWithTZ(
                      history.createdDate
                    )}
                  </p>
                  <p>Status: {history.status}</p>
                  {history.status === 'FAILED' && history.error ? (
                    <>
                      {expandedView === history.instanceId ? (
                        <button
                          className="view-link-button"
                          onClick={handleHideClick}>
                          {en.actions.history.hide}
                        </button>
                      ) : (
                        <button
                          className="view-link-button"
                          onClick={() => handleViewClick(history.instanceId)}>
                          {en.actions.history.view}
                        </button>
                      )}
                      {expandedView === history.instanceId ? (
                        <div className="error-wrapper">
                          <p className="title">
                            {en.actions.history.errorDescription}
                          </p>
                          <p className="error-text">{history.error}</p>
                        </div>
                      ) : null}
                    </>
                  ) : null}
                </div>
              )
            }
          ]}
          rowKey={'id'}
          dataSource={pipelinesHistory?.history}
          pagination={{
            total: pipelinesHistory?.count,
            current: pagination.current,
            pageSize: pagination.pageSize,
            onChange: onPaginationChange,
            showTotal: (total) => <p>{total} Records</p>,
            showSizeChanger: true,
            className: 'pipeline-history-table-pagination',
            size: 'small'
          }}
        />
      </PipelinesHistoryContentContainer>
    </Drawer>
  );
};

PipelineHistoryDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedPipeline: PropTypes.object,
  apiActions: PropTypes.object.isRequired,
  pipelinesHistory: PropTypes.object.isRequired,
  pipelinesHistoryIsLoading: PropTypes.bool.isRequired
};

export default PipelineHistoryDrawer;
